import type { Editor } from "@tiptap/react";
import { lazy, type ReactNode, Suspense } from "react";
import type { AgentSkillRevision } from "@common/schemas/domains/agentSkills/agent-skill.schema";
import { HStack } from "@design-system/HStack/HStack";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerSectionData } from "@design-system/Picker/Picker.types";
import { formatTokenDate } from "@helpers/date";
import { useAgentSkillRevisions } from "@api/agentSkill.api";
import { actorLabel, groupAgentSkillRevisions } from "./agentSkillChat";
import {
    AgentSkillLifecycleActions,
    type AgentSkillLifecycleProps,
} from "./AgentSkillReadActions";
import styles from "./AgentSkills.module.scss";

// The table menu pulls in Tiptap, which only edit mode needs.
const AgentSkillTableMenu = lazy(() =>
    import("./AgentSkillTableMenu").then((m) => ({
        default: m.AgentSkillTableMenu,
    })),
);

interface Props {
    editor: Editor | null;
    lifecycle: AgentSkillLifecycleProps;
    currentRevisionId: string | null;
    /** The older version loaded as a draft; null shows the current one. */
    viewedRevisionId: string | null;
    onViewRevision: (revision: AgentSkillRevision) => void;
    /** The form's Cancel and Save buttons. */
    children: ReactNode;
}

/** The command island's edit-mode row. */
export const AgentSkillEditActions = ({
    editor,
    lifecycle,
    currentRevisionId,
    viewedRevisionId,
    onViewRevision,
    children,
}: Props) => {
    const skillId = lifecycle.skill.id;
    const { data: revisions = [] } = useAgentSkillRevisions(skillId);
    const versionSections: Array<PickerSectionData<string>> =
        groupAgentSkillRevisions(revisions, {
            skillId,
            defaultSkillId: lifecycle.skill.defaultSkillId,
        }).map((group) => ({
            key: group.key,
            title: group.label ?? undefined,
            items: group.revisions.map((revision) => ({
                value: revision.id,
                label: `Version ${revision.version} · ${formatTokenDate(revision.createdAt)}`,
                description: actorLabel(revision),
            })),
        }));
    return (
        <>
            <Suspense fallback={null}>
                <AgentSkillTableMenu editor={editor} />
            </Suspense>
            <Picker.Select
                sections={versionSections}
                value={viewedRevisionId ?? currentRevisionId}
                placeholder="Version"
                onValueChange={(value) => {
                    const revision = revisions.find(
                        (candidate) => candidate.id === value,
                    );
                    if (revision) {
                        onViewRevision(revision);
                    }
                }}
            />
            <HStack gap={8} className={styles.islandEnd}>
                <AgentSkillLifecycleActions {...lifecycle} />
                {children}
            </HStack>
        </>
    );
};
